import { useMutation, useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import http from "../utils/SupabaseHttp";

interface UseTranscriptionStripeCardDetailsOptions {
  stripeCustomerId?: string;
  setHasAddedCard: (has: boolean) => void;
}

interface StripeCustomerData {
  stripeCustomerId?: string;
  customerId?: string;
}

interface SavedCardsData {
  hasSavedCard?: boolean;
}

export function useTranscriptionStripeCardDetails({
  stripeCustomerId,
  setHasAddedCard
}: UseTranscriptionStripeCardDetailsOptions) {
  const userDetailsQuery = useQuery<StripeCustomerData | null, Error>({
    queryKey: ["transcription", "user", "details"],
    queryFn: async () => {
      const res = await http.get("/transcription/user/details");
      return res.data;
    },
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    staleTime: 5 * 60 * 1000
  });

  const pickId = (val: unknown) => {
    if (typeof val === "string") return val.trim() || undefined;
    if (typeof val === "number") return `${val}`;
    return undefined;
  };

  const existingIdFromUser =
    pickId(userDetailsQuery.data?.stripeCustomerId) ||
    pickId((userDetailsQuery.data as any)?.user?.stripeCustomerId) ||
    pickId((userDetailsQuery.data as any)?.user?.customerId);

  const createCustomerMutation = useMutation<StripeCustomerData, Error, void>({
    mutationFn: async () => {
      const res = await http.post("/transcription/create-customer");
      return res.data;
    }
  });

  const effectiveCustomerId =
    pickId(stripeCustomerId) ||
    pickId(existingIdFromUser) ||
    pickId(createCustomerMutation.data?.stripeCustomerId) ||
    pickId(createCustomerMutation.data?.customerId);

  const shouldCreateCustomer =
    !stripeCustomerId &&
    userDetailsQuery.isSuccess &&
    !existingIdFromUser &&
    !createCustomerMutation.isPending &&
    !createCustomerMutation.isSuccess;

  useEffect(() => {
    if (shouldCreateCustomer) {
      createCustomerMutation.mutate();
    }
  }, [shouldCreateCustomer, createCustomerMutation]);

  const checkCardsQuery = useQuery<SavedCardsData | null, Error>({
    queryKey: ["transcription", "stripe", "savedCards", effectiveCustomerId],
    queryFn: async (): Promise<SavedCardsData | null> => {
      if (!effectiveCustomerId) return null;
      const res = await http.post("/transcription/check-saved-cards", {
        customerId: effectiveCustomerId
      });
      return res.data;
    },
    enabled: !!effectiveCustomerId,
    refetchOnWindowFocus: false
  });

  const hasSavedCard = !!checkCardsQuery.data?.hasSavedCard;

  useEffect(() => {
    if (checkCardsQuery.isSuccess && checkCardsQuery.data) {
      setHasAddedCard(hasSavedCard);
    }
  }, [
    checkCardsQuery.isSuccess,
    checkCardsQuery.data,
    hasSavedCard,
    setHasAddedCard
  ]);

  return {
    isChecking:
      userDetailsQuery.isLoading ||
      createCustomerMutation.isPending ||
      checkCardsQuery.isLoading,
    error:
      userDetailsQuery.error ||
      createCustomerMutation.error ||
      checkCardsQuery.error,
    customerId: effectiveCustomerId,
    hasSavedCard,
    isCreatingCustomer: createCustomerMutation.isPending,
    isCheckingCards: checkCardsQuery.isLoading
  };
}
